import * as Sentry from '@sentry/react';

type PublicKeyCredentialWithResponses = PublicKeyCredential & {
    response: AuthenticatorAttestationResponse | AuthenticatorAssertionResponse;
};

type PasskeyOperation = 'registration' | 'authentication';
export type PasskeyBrowserErrorCode =
    | 'unsupported'
    | 'invalidOptions'
    | 'notAllowed'
    | 'invalidState'
    | 'security'
    | 'failed';

export class PasskeyBrowserError extends Error {
    operation: PasskeyOperation;
    causeName: string;
    errorCode: PasskeyBrowserErrorCode;

    constructor(
        operation: PasskeyOperation,
        causeName: string,
        errorCode: PasskeyBrowserErrorCode,
    ) {
        super(errorCode);
        this.name = 'PasskeyBrowserError';
        this.operation = operation;
        this.causeName = causeName;
        this.errorCode = errorCode;
    }
}

const base64UrlToArrayBuffer = (value: string): ArrayBuffer => {
    const base64 = value.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64.padEnd(Math.ceil(base64.length / 4) * 4, '=');
    const binary = window.atob(padded);
    const bytes = new Uint8Array(binary.length);
    for (let index = 0; index < binary.length; index += 1) {
        bytes[index] = binary.charCodeAt(index);
    }
    return bytes.buffer;
};

const arrayBufferToBase64Url = (buffer: ArrayBuffer | null): string | null => {
    if (!buffer) {
        return null;
    }
    const bytes = new Uint8Array(buffer);
    let binary = '';
    for (const byte of bytes) {
        binary += String.fromCharCode(byte);
    }
    return window.btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
};

const isObject = (value: unknown): value is Record<string, unknown> =>
    typeof value === 'object' && value !== null;

export const isPasskeySupported = () =>
    typeof window !== 'undefined' &&
    'PublicKeyCredential' in window &&
    Boolean(navigator.credentials);

const errorProperty = (error: unknown, property: 'name' | 'message') => {
    if (!isObject(error)) {
        return undefined;
    }
    const value = error[property];
    return typeof value === 'string' && value ? value : undefined;
};

const errorName = (error: unknown) => errorProperty(error, 'name') ?? typeof error;

const errorMessage = (error: unknown) =>
    errorProperty(error, 'message') ?? 'Unknown browser passkey error.';

const passkeyErrorCode = (error: unknown): PasskeyBrowserErrorCode => {
    const name = errorName(error);
    if (name === 'NotAllowedError') {
        return 'notAllowed';
    }
    if (name === 'InvalidStateError') {
        return 'invalidState';
    }
    if (name === 'SecurityError') {
        return 'security';
    }
    return 'failed';
};

const passkeyOptionsDiagnostics = (options: Record<string, any>) => ({
    hasChallenge: Boolean(options.challenge),
    hasUser: Boolean(options.user),
    hasUserId: Boolean(options.user?.id),
    rpId: typeof options.rp?.id === 'string' ? options.rp.id : undefined,
    rpName: typeof options.rp?.name === 'string' ? options.rp.name : undefined,
    excludeCredentialsCount: Array.isArray(options.excludeCredentials)
        ? options.excludeCredentials.length
        : undefined,
    allowCredentialsCount: Array.isArray(options.allowCredentials)
        ? options.allowCredentials.length
        : undefined,
    authenticatorAttachment: options.authenticatorSelection?.authenticatorAttachment,
    residentKey: options.authenticatorSelection?.residentKey,
    requireResidentKey: options.authenticatorSelection?.requireResidentKey,
    userVerification: options.authenticatorSelection?.userVerification,
    attestation: options.attestation,
    pubKeyCredParamAlgs: Array.isArray(options.pubKeyCredParams)
        ? options.pubKeyCredParams.map((param: PublicKeyCredentialParameters) => param.alg)
        : undefined,
});

const capturePasskeyBrowserError = (
    operation: PasskeyOperation,
    error: unknown,
    options: Record<string, any>,
) => {
    const name = errorName(error);
    const message = errorMessage(error);
    const capturedError =
        error instanceof Error ? error : new Error(`Passkey ${operation} failed: ${message}`);

    Sentry.withScope((scope) => {
        scope.setTag('passkey.operation', operation);
        scope.setTag('passkey.error_name', name);
        scope.setContext('passkey_browser', {
            operation,
            errorName: name,
            errorMessage: message,
            userAgent: navigator.userAgent,
            isSecureContext: window.isSecureContext,
            hasPublicKeyCredential: 'PublicKeyCredential' in window,
            hasNavigatorCredentials: Boolean(navigator.credentials),
            ...passkeyOptionsDiagnostics(options),
        });
        if (name === 'NotAllowedError') {
            Sentry.captureMessage(
                `Passkey ${operation} was not allowed by the browser.`,
                'warning',
            );
            return;
        }
        Sentry.captureException(capturedError);
    });
};

export const startPasskeyRegistration = async (options: Record<string, any>) => {
    if (!isPasskeySupported()) {
        throw new PasskeyBrowserError('registration', 'UnsupportedError', 'unsupported');
    }
    let publicKey: PublicKeyCredentialCreationOptions;
    try {
        publicKey = {
            ...options,
            challenge: base64UrlToArrayBuffer(String(options.challenge)),
            user: {
                ...options.user,
                id: base64UrlToArrayBuffer(String(options.user.id)),
            },
            excludeCredentials: options.excludeCredentials?.map((credential: any) => ({
                ...credential,
                id: base64UrlToArrayBuffer(String(credential.id)),
            })),
        } as PublicKeyCredentialCreationOptions;
    } catch (error) {
        // Decoding malformed/incomplete options (e.g. a server contract change)
        // would otherwise throw raw before the instrumented ceremony below and
        // surface as a generic error with no telemetry — capture and classify it.
        capturePasskeyBrowserError('registration', error, options);
        throw new PasskeyBrowserError('registration', errorName(error), 'invalidOptions');
    }
    let credential: PublicKeyCredentialWithResponses | null;
    try {
        credential = (await navigator.credentials.create({
            publicKey,
        })) as PublicKeyCredentialWithResponses | null;
    } catch (error) {
        capturePasskeyBrowserError('registration', error, options);
        throw new PasskeyBrowserError('registration', errorName(error), passkeyErrorCode(error));
    }
    if (!credential) {
        const error = new DOMException('Passkey setup was cancelled.', 'NotAllowedError');
        capturePasskeyBrowserError('registration', error, options);
        throw new PasskeyBrowserError('registration', error.name, passkeyErrorCode(error));
    }
    return serializePasskeyCredential(credential);
};

export const startPasskeyAuthentication = async (options: Record<string, any>) => {
    if (!isPasskeySupported()) {
        throw new PasskeyBrowserError('authentication', 'UnsupportedError', 'unsupported');
    }
    let publicKey: PublicKeyCredentialRequestOptions;
    try {
        publicKey = {
            ...options,
            challenge: base64UrlToArrayBuffer(String(options.challenge)),
            allowCredentials: options.allowCredentials?.map((credential: any) => ({
                ...credential,
                id: base64UrlToArrayBuffer(String(credential.id)),
            })),
        } as PublicKeyCredentialRequestOptions;
    } catch (error) {
        // Decoding malformed/incomplete options (e.g. a server contract change)
        // would otherwise throw raw before the instrumented ceremony below and
        // surface as a generic error with no telemetry — capture and classify it.
        capturePasskeyBrowserError('authentication', error, options);
        throw new PasskeyBrowserError('authentication', errorName(error), 'invalidOptions');
    }
    let credential: PublicKeyCredentialWithResponses | null;
    try {
        credential = (await navigator.credentials.get({
            publicKey,
        })) as PublicKeyCredentialWithResponses | null;
    } catch (error) {
        capturePasskeyBrowserError('authentication', error, options);
        throw new PasskeyBrowserError('authentication', errorName(error), passkeyErrorCode(error));
    }
    if (!credential) {
        const error = new DOMException('Passkey login was cancelled.', 'NotAllowedError');
        capturePasskeyBrowserError('authentication', error, options);
        throw new PasskeyBrowserError('authentication', error.name, passkeyErrorCode(error));
    }
    return serializePasskeyCredential(credential);
};

const serializePasskeyCredential = (credential: PublicKeyCredentialWithResponses) => {
    const response = credential.response;
    const payload: Record<string, unknown> = {
        id: credential.id,
        rawId: arrayBufferToBase64Url(credential.rawId),
        type: credential.type,
        clientExtensionResults: credential.getClientExtensionResults(),
        response: {},
    };

    if (response instanceof AuthenticatorAttestationResponse) {
        payload.response = {
            attestationObject: arrayBufferToBase64Url(response.attestationObject),
            clientDataJSON: arrayBufferToBase64Url(response.clientDataJSON),
            transports: response.getTransports?.() ?? [],
        };
    } else if (response instanceof AuthenticatorAssertionResponse) {
        payload.response = {
            authenticatorData: arrayBufferToBase64Url(response.authenticatorData),
            clientDataJSON: arrayBufferToBase64Url(response.clientDataJSON),
            signature: arrayBufferToBase64Url(response.signature),
            userHandle: arrayBufferToBase64Url(response.userHandle),
        };
    } else if (isObject(response)) {
        payload.response = response;
    }

    return payload;
};
